import { Fragment, useEffect, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import srOnly from 'styles/tools/sr-only'
import TripPlannerChatSearchResultRow from './TripPlannerChatSearchResultRow'
import { TRIP_PLANNER_NAVIGATION_ROW_INSET } from './tripPlannerNavigationConstants'
import useChatSearchResults from './useChatSearchResults'

const LOAD_MORE_ROOT_MARGIN = '0px 0px 32px 0px'

// A region that is only mounted when it already holds its text is not reliably announced.
const LiveRegion = styled.div`
  ${srOnly}
`

const Results = styled.div`
  flex: 1;
  min-block-size: 0;
  overflow-y: auto;
  padding-inline: ${rem(TRIP_PLANNER_NAVIGATION_ROW_INSET)};
`

const EmptyState = styled(BodyText)`
  padding: ${rem(32)} ${rem(8)};
  text-align: center;
`

const LoadingState = styled.div`
  min-block-size: ${rem(96)};
`

const LoadMoreError = styled(Group)`
  padding: ${rem(12)} ${rem(16)};
`

interface Props {
  /** Trimmed, and mounted only once it reaches MIN_CHAT_SEARCH_QUERY_LENGTH. */
  query: string
  onChatSelect: (chatId: number) => void
}

export default function TripPlannerChatSearchResults({ query, onChatSelect }: Props) {
  const intl = useIntl()
  const search = useChatSearchResults(query)
  const [resultsElement, setResultsElement] = useState<HTMLDivElement | null>(null)
  const [loadMoreTriggerRef, isLoadMoreTriggerInView] = useInView({
    root: resultsElement,
    rootMargin: LOAD_MORE_ROOT_MARGIN,
  })
  const { hasMore, isLoadingMore, isLoadMoreError, loadMore } = search

  useEffect(() => {
    if (isLoadMoreTriggerInView && hasMore && !isLoadingMore && !isLoadMoreError) {
      loadMore()
    }
  }, [hasMore, isLoadMoreError, isLoadMoreTriggerInView, isLoadingMore, loadMore])

  const noResults = intl.formatMessage({
    id: '-ROPTPF-mOaRSJv0eDUk',
    defaultMessage: 'No chats found',
    description: 'Empty result message shown when TripPlanner chat search finds no chats',
  })
  const searchError = intl.formatMessage({
    id: 'jrrXW1sY8LxMa3d59cU4',
    defaultMessage: 'Unable to search chats. Please try again.',
    description: 'Error message shown when the TripPlanner chat search request fails',
  })
  const loadMoreError = intl.formatMessage({
    id: 'DygwXZlHblW4kq5uJc6u',
    defaultMessage: 'Couldn’t load more chats.',
    description: 'Message shown below the TripPlanner chat search results in the side nav and menu drawer when the next page of results fails to load',
  })
  const retryLoadMoreLabel = intl.formatMessage({
    id: 'jZz6HQEqGk5kcoxqBONd',
    defaultMessage: 'Try again',
    description: 'Button below the failed TripPlanner chat search results message in the side nav and menu drawer that loads the next page again',
  })
  const resultCountAnnouncement = intl.formatMessage({
    id: 'Ib50yGVpgvfCpcrApgRO',
    defaultMessage: '{count, plural, one {# chat found} other {# chats found}}',
    description: 'Screen reader announcement after a TripPlanner chat search in the side nav and menu drawer, with the number of chats found',
  }, { count: search.firstPageCount })
  const partialResultCountAnnouncement = intl.formatMessage({
    id: 'DFRfrV_cfCK5DoSEPFPA',
    defaultMessage: '{count, plural, one {More than # chat found} other {More than # chats found}}',
    description: 'Screen reader announcement after a TripPlanner chat search in the side nav and menu drawer when more chats match than the first page shows, with the number shown',
  }, { count: search.firstPageCount })

  const resultsAnnouncement = search.hasMoreThanFirstPage ? partialResultCountAnnouncement : resultCountAnnouncement
  const announcements = {
    pending: '',
    results: isLoadMoreError ? loadMoreError : resultsAnnouncement,
    error: searchError,
    empty: noResults,
  }

  return (
    <>
      <LiveRegion aria-live="polite" aria-atomic="true">{announcements[search.status]}</LiveRegion>
      {search.status === 'results' && (
        <Results ref={setResultsElement} aria-busy={search.isFetching}>
          {search.results.map((hit, index) => (
            <Fragment key={hit.chatId}>
              {index > 0 && <Divider kind="primary" />}
              <TripPlannerChatSearchResultRow hit={hit} onSelect={onChatSelect} />
            </Fragment>
          ))}
          {isLoadingMore && (
            <LoadingState>
              <LoadingIndicator inline backdrop="none" height={48} />
            </LoadingState>
          )}
          {isLoadMoreError && !isLoadingMore && (
            <LoadMoreError direction="vertical" gap={4} horizontalAlign="start">
              <BodyText variant="small" colour="critical">{loadMoreError}</BodyText>
              <TextButton kind="secondary" size="small" onClick={loadMore}>{retryLoadMoreLabel}</TextButton>
            </LoadMoreError>
          )}
          {hasMore && <div ref={loadMoreTriggerRef} />}
        </Results>
      )}
      {search.status === 'pending' && (
        <LoadingState>
          <LoadingIndicator inline backdrop="none" height={96} />
        </LoadingState>
      )}
      {search.status === 'error' && <EmptyState variant="medium" colour="light-foreground-alt">{searchError}</EmptyState>}
      {search.status === 'empty' && <EmptyState variant="medium" colour="light-foreground-alt">{noResults}</EmptyState>}
    </>
  )
}
